/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { RoomEvent, Track, type Participant, type Room } from "livekit-client";

export const orientationTopic = "call.screen-orientation.v1";
export interface ShareOrientation {
  sid: string;
  angle: number;
  landscape: boolean;
  sequence: number;
}
interface Entry extends ShareOrientation {
  reference?: number;
}
const entries = new WeakMap<Participant, Entry>();
const listeners = new Set<() => void>();
let revision = 0;
let sequence = Date.now() * 1024;
const changed = (): void => {
  revision++;
  listeners.forEach((fn) => fn());
};
export const orientationRevision = (): number => revision;
export const subscribeOrientation = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
export function acceptOrientation(
  participant: Participant,
  value: unknown,
): boolean {
  if (!value || typeof value !== "object") return false;
  const p = value as ShareOrientation;
  if (
    typeof p.sid !== "string" ||
    !/^TR_[A-Za-z0-9_-]{1,120}$/.test(p.sid) ||
    ![0, 90, 180, 270].includes(p.angle) ||
    typeof p.landscape !== "boolean" ||
    !Number.isSafeInteger(p.sequence) ||
    p.sequence < 0
  )
    return false;
  const before = entries.get(participant);
  if (before?.sid === p.sid && before.sequence >= p.sequence) return false;
  const next: Entry = {
    sid: p.sid,
    angle: p.angle,
    landscape: p.landscape,
    sequence: p.sequence,
  };
  if (before?.sid === p.sid) next.reference = before.reference;
  entries.set(participant, next);
  if (
    !before ||
    before.sid !== next.sid ||
    before.angle !== next.angle ||
    before.landscape !== next.landscape
  )
    changed();
  return true;
}
/** Trust packet ownership from LiveKit, never a user ID supplied in its payload. */
export function receiveOrientation(
  payload: Uint8Array,
  participant?: Participant,
  topic?: string,
): void {
  if (topic !== orientationTopic || !participant || payload.byteLength > 384)
    return;
  try {
    acceptOrientation(
      participant,
      JSON.parse(new TextDecoder().decode(payload)),
    );
  } catch {
    /* Ignore unrelated/malformed data. */
  }
}
export function presentationRotation(
  participant: Participant | undefined,
  sid: string | undefined,
  width: number,
  height: number,
): number {
  if (!participant || !sid || width <= 0 || height <= 0 || width === height)
    return 0;
  const orientation = entries.get(participant);
  if (!orientation || orientation.sid !== sid) return 0;
  if (width > height === orientation.landscape) {
    // The browser has already oriented these decoded pixels. Do not rotate twice.
    orientation.reference ??= orientation.angle;
    return 0;
  }
  const delta = (orientation.angle - (orientation.reference ?? 0) + 360) % 360;
  return delta === 90 || delta === 270 ? (360 - delta) % 360 : 0;
}
export function readAppleScreenOrientation():
  | Omit<ShareOrientation, "sid" | "sequence">
  | undefined {
  const apple =
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  if (!apple) return;
  const legacy = (window as Window & { orientation?: number }).orientation;
  const raw = screen.orientation?.angle ?? legacy;
  if (typeof raw !== "number") return;
  const angle = ((raw % 360) + 360) % 360;
  if (![0, 90, 180, 270].includes(angle)) return;
  const type = screen.orientation?.type;
  return {
    angle,
    landscape: type
      ? type.startsWith("landscape")
      : angle === 90 || angle === 270,
  };
}
/** Small change messages, replay for late joiners, and a ten-second recovery heartbeat. */
export function installScreenShareOrientation(
  room: Room,
  read = readAppleScreenOrientation,
): () => void {
  let timer: ReturnType<typeof setInterval> | undefined,
    closed = false,
    sending = false,
    pendingReplay = false,
    publishedAt = 0,
    signature = "";
  const send = async (force = false): Promise<void> => {
    if (closed) return;
    if (sending) {
      pendingReplay ||= force;
      return;
    }
    const publication = room.localParticipant.getTrackPublication(
      Track.Source.ScreenShare,
    );
    const orientation = read();
    if (!publication || publication.isMuted || !orientation) return;
    const next = JSON.stringify([
      publication.trackSid,
      orientation.angle,
      orientation.landscape,
    ]);
    if (!force && signature === next && Date.now() - publishedAt < 10_000)
      return;
    const packet = {
      sid: publication.trackSid,
      ...orientation,
      sequence: ++sequence,
    };
    sending = true;
    try {
      acceptOrientation(room.localParticipant, packet);
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(packet)),
        { reliable: true, topic: orientationTopic },
      );
      if (!closed) {
        signature = next;
        publishedAt = Date.now();
      }
    } catch {
      /* Reconnect/next sample retries; video publishing is independent. */
    } finally {
      sending = false;
      if (pendingReplay && !closed) {
        pendingReplay = false;
        void send(true);
      }
    }
  };
  const sample = (): void => {
    void send();
  };
  const replay = (): void => {
    void send(true);
  };
  const sync = (): void => {
    const active =
      !!room.localParticipant.getTrackPublication(Track.Source.ScreenShare) &&
      !!read();
    if (active && !timer) {
      timer = setInterval(sample, 1000);
      replay();
    } else if (!active && timer) {
      clearInterval(timer);
      timer = undefined;
      signature = "";
    }
  };
  const receive = (
    payload: Uint8Array,
    participant?: Participant,
    _kind?: unknown,
    topic?: string,
  ): void => receiveOrientation(payload, participant, topic);
  const remove = (participant: Participant): void => {
    if (entries.delete(participant)) changed();
  };
  room.on(RoomEvent.DataReceived, receive);
  room.on(RoomEvent.LocalTrackPublished, sync);
  room.on(RoomEvent.LocalTrackUnpublished, sync);
  room.on(RoomEvent.ParticipantConnected, replay);
  room.on(RoomEvent.ParticipantDisconnected, remove);
  room.on(RoomEvent.Reconnected, replay);
  window.addEventListener("orientationchange", sample);
  window.addEventListener("resize", sample);
  document.addEventListener("visibilitychange", sample);
  screen.orientation?.addEventListener?.("change", sample);
  sync();
  return () => {
    closed = true;
    clearInterval(timer);
    room.off(RoomEvent.DataReceived, receive);
    room.off(RoomEvent.LocalTrackPublished, sync);
    room.off(RoomEvent.LocalTrackUnpublished, sync);
    room.off(RoomEvent.ParticipantConnected, replay);
    room.off(RoomEvent.ParticipantDisconnected, remove);
    room.off(RoomEvent.Reconnected, replay);
    window.removeEventListener("orientationchange", sample);
    window.removeEventListener("resize", sample);
    document.removeEventListener("visibilitychange", sample);
    screen.orientation?.removeEventListener?.("change", sample);
    remove(room.localParticipant);
    room.remoteParticipants.forEach(remove);
  };
}
